import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { soloApi } from "@/lib/api";
import { AGENTS } from "@/lib/agents";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, X, Edit3, Inbox as InboxIcon } from "lucide-react";
import { toast } from "sonner";

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");

  const load = () => soloApi.approvals().then(({ data }) => { setItems(data); setIdx(0); });
  useEffect(() => { load(); }, []);

  const t = items[idx];
  const agent = t ? (AGENTS[t.agent] ?? AGENTS.ceo) : AGENTS.ceo;
  const previewText = t?.payload?.post_text || t?.payload?.email_text || t?.summary || "";

  const decide = async (decision) => {
    const edits = editing ? editText : undefined;
    await soloApi.decide(t.task_id, decision, edits);
    toast.success(decision === "approve" ? "Approved ✦" : "Rejected — sent back to queue");
    setEditing(false);
    setEditText("");
    load();
  };

  return (
    <div className="p-8 pb-16">
      <div className="mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-neon-cyan mb-2">✦ Approval inbox</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold"><span className="chrome-text">Yes or no?</span></h1>
        <p className="text-white/70 mt-2">One tap. High-risk actions wait here until you say go.</p>
      </div>

      {items.length === 0 ? (
        <div className="glass rounded-3xl p-16 text-center">
          <InboxIcon className="w-12 h-12 mx-auto text-neon-lime mb-4" />
          <div className="font-display text-2xl font-bold mb-2">Inbox zero.</div>
          <p className="text-white/60">Your crew has nothing pending. Ask Mica or Rex to draft something.</p>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          <div className="text-center font-mono text-xs text-white/50 mb-4">{idx + 1} of {items.length}</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={t.task_id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, y: -30, rotate: -3 }}
              transition={{ type: "spring", bounce: 0.4 }}
              className="glass rounded-[2rem] p-6 sticker"
              data-testid={`approval-card-${t.task_id}`}
              style={{ boxShadow: `0 0 40px ${agent.color}55` }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 slime-blob overflow-hidden" style={{ background: agent.gradient }}>
                  <img src={agent.avatar} alt="" className="w-full h-full object-cover mix-blend-luminosity" />
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: agent.color }}>{agent.name} · {agent.role}</div>
                  <div className="font-display text-xl font-bold">{t.title}</div>
                </div>
              </div>

              {t?.payload?.image_url && (
                <img src={t.payload.image_url} alt="visual" className="w-full rounded-2xl mb-4 border border-white/10" />
              )}

              {editing ? (
                <Textarea
                  data-testid="approval-edit-textarea"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  className="bg-white/5 border-white/20 text-white rounded-xl min-h-[160px]"
                />
              ) : (
                <div className="bg-slate-950/60 border border-white/10 rounded-2xl p-4 whitespace-pre-wrap font-mono text-sm text-neon-lime/90" data-testid="approval-preview">
                  {previewText}
                </div>
              )}

              {t.money_at_stake > 0 && (
                <div className="mt-3 inline-block px-3 py-1 rounded-full bg-neon-tangerine/20 border border-neon-tangerine/40 text-neon-tangerine font-bold text-sm">
                  ${t.money_at_stake} at stake
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 mt-6">
                <Button
                  data-testid="approval-reject"
                  onClick={() => decide("reject")}
                  className="rounded-2xl bg-neon-pink hover:bg-neon-pink/90 text-white font-bold py-6 neon-glow-pink"
                >
                  <X className="w-5 h-5 mr-1" /> Reject
                </Button>
                <Button
                  data-testid="approval-edit"
                  onClick={() => { setEditing(!editing); if (!editing) setEditText(previewText); }}
                  className="rounded-2xl bg-white/10 hover:bg-white/20 text-white font-bold py-6 border border-white/20"
                >
                  <Edit3 className="w-5 h-5 mr-1" /> {editing ? "Cancel" : "Edit"}
                </Button>
                <Button
                  data-testid="approval-approve"
                  onClick={() => decide("approve")}
                  className="rounded-2xl bg-neon-lime hover:bg-neon-lime/90 text-slate-900 font-bold py-6 neon-glow-lime"
                >
                  <Check className="w-5 h-5 mr-1" /> Approve
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>

          {items.length > 1 && (
            <div className="flex justify-center gap-2 mt-6">
              <Button data-testid="approval-prev" variant="ghost" onClick={() => setIdx(Math.max(0, idx - 1))} className="text-white hover:bg-white/10 rounded-full">← Prev</Button>
              <Button data-testid="approval-next" variant="ghost" onClick={() => setIdx(Math.min(items.length - 1, idx + 1))} className="text-white hover:bg-white/10 rounded-full">Next →</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
